// Public, no-login training completion page for field caregivers.
// Route: /training-link/:token — one token covers every module currently
// assigned/in_progress for that staff member; walks through them one at a
// time with a progress bar rather than requiring a separate link per module.
// This is the flow that makes training tracking work for the 81% of staff
// who have no app login at all.
//
// Sections belonging to the same course (e.g. "Core Compliance Training") are
// walked through in course sequence and certified together with ONE
// consolidated certificate on completion — not one certificate per section
// (see submit-training-completion's courseComplete flag).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { renderCourseCertificateBlob, blobToBase64 } from '../lib/documents/trainingCertificatePdf';
import { CRM_EMAIL } from '../lib/org';

const FN = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/`;
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Same TTF the PDF and the Path A guided-signing flow use (see GuidedDocument.js) —
// reused here instead of the browser's generic cursive fallback.
function SignatureFontStyle() {
  return (
    <style>{`@font-face{font-family:'Signature';src:url('${(process.env.PUBLIC_URL || '')}/fonts/DancingScript.ttf') format('truetype');font-display:swap;}`}</style>
  );
}

export default function TrainingLink() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [index, setIndex] = useState(0);
  const [ack, setAck] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [allDone, setAllDone] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  // A caregiver used to be able to tick "I read and understood" without ever
  // scrolling the text. The sign-off now unlocks only once the content has
  // been read to the end — or immediately when it is short enough to need no
  // scrolling at all, so a brief module never traps anyone.
  const contentRef = useRef(null);
  const reachedEndRef = useRef(false);
  const [readToEnd, setReadToEnd] = useState(false);
  const [needsScroll, setNeedsScroll] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  async function load() {
    try {
      const r = await fetch(`${FN}get-staff-doc-by-token?token=${token}`, {
        headers: { Authorization: `Bearer ${ANON}`, apikey: ANON },
      });
      const j = await r.json();
      if (!r.ok) { setErr(j.error || 'This link is invalid or expired.'); setState({ loading: false }); return; }
      if (j.documents?.[0]?.doc_type !== 'training') { setErr('This link is not a training link.'); setState({ loading: false }); return; }
      const sorted = [...(j.trainingRecords || [])].sort((a, b) => (a.training_module?.sequence || 0) - (b.training_module?.sequence || 0));
      setState({ loading: false, ...j, trainingRecords: sorted });
      if (sorted.length === 0) setAllDone(true);
    } catch (e) { setErr(e.message); setState({ loading: false }); }
  }

  const records = state.trainingRecords || [];
  const current = records[index];
  const courseTitle = current?.training_module?.course?.title;

  async function fileCourseCertificate(sections, courseRecordId, signedName) {
    const blob = await renderCourseCertificateBlob({
      staffName: signedName,
      courseTitle: courseTitle || 'Training',
      courseDescription: null,
      completedAt: new Date().toLocaleString(),
      signatureRef: signedName,
      courseRecordId,
      sections: sections.map(s => ({
        moduleNumber: s.training_module?.module_number,
        title: s.training_module?.title,
        completedAt: new Date().toLocaleDateString(),
      })),
    });
    const fileBase64 = await blobToBase64(blob);
    await fetch(`${FN}upload-training-certificate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ANON}`, apikey: ANON },
      body: JSON.stringify({
        fileBase64,
        filename: `${(courseTitle || 'Training').replace(/[^a-z0-9]+/gi, '_')}_${signedName.replace(/[^a-z0-9]+/gi, '_')}.pdf`,
        staffName: signedName,
        token,
        courseTitle: courseTitle || 'Training',
        courseRecordId,
        contentType: 'application/pdf',
      }),
    });
  }

  async function submit() {
    const fullName = name.trim();
    if (!fullName) { setErr('Type your full name to complete this training.'); return; }
    if (!ack) { setErr('Please confirm you completed the training above.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${FN}submit-training-completion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ANON}`, apikey: ANON },
        body: JSON.stringify({ token, signedName: fullName, trainingRecordId: current.id }),
      });
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.error || 'Submission failed');

      const nowCompleted = [...completedSections, current];
      setCompletedSections(nowCompleted);

      // One certificate per COURSE, filed once every section in it is done — not one
      // per section. Don't block the caregiver's flow on this; a Drive hiccup
      // shouldn't stop them finishing.
      // IMPORTANT: nowCompleted accumulates sections across EVERY course walked
      // through in this one token/session (e.g. Policy Manual then Handbook,
      // back to back) — must filter down to just this course's own sections
      // before certifying, or a later course's certificate incorrectly lists an
      // earlier, unrelated course's section too.
      if (j.courseComplete && j.courseRecordId) {
        const thisCourseSections = nowCompleted.filter(s => s.training_module?.course_id === j.courseId);
        fileCourseCertificate(thisCourseSections, j.courseRecordId, fullName).catch(e => console.error('Certificate filing failed:', e));
      }

      setAck(false);
      setName('');
      reachedEndRef.current = false;
      setReadToEnd(false);
      contentRef.current?.scrollTo({ top: 0 });
      if (index + 1 < records.length) {
        setIndex(index + 1);
      } else {
        setAllDone(true);
      }
    } catch (e) {
      setErr(`Could not submit: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  // Watch the content box: unlock at the bottom, and cope with short modules,
  // resizes and late-loading markdown.
  const checkScrolled = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const scrolls = el.scrollHeight > el.clientHeight + 4;
    setNeedsScroll(scrolls);
    // Short enough to need no scrolling: nothing to read past, so unlock. This
    // is re-evaluated rather than remembered, because markdown and images can
    // make the box taller after the first paint and a phone can be rotated —
    // either would otherwise leave it unlocked with text still unread.
    if (!scrolls) { setReadToEnd(true); return; }
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) reachedEndRef.current = true;
    // Sticky once they have been to the bottom, so scrolling back up to
    // re-read something does not take the signature away again.
    setReadToEnd(reachedEndRef.current);
  }, []);

  useEffect(() => {
    reachedEndRef.current = false;
    setReadToEnd(false);
    const el = contentRef.current;
    if (!el) return undefined;
    checkScrolled();
    // Markdown and any images can change the height after the first paint.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkScrolled) : null;
    if (ro) ro.observe(el);
    const t = setTimeout(checkScrolled, 400);
    el.addEventListener('scroll', checkScrolled, { passive: true });
    window.addEventListener('resize', checkScrolled);
    return () => {
      clearTimeout(t);
      if (ro) ro.disconnect();
      el.removeEventListener('scroll', checkScrolled);
      window.removeEventListener('resize', checkScrolled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, state.trainingRecords, checkScrolled]);

  if (state.loading) return <Shell><p>Loading…</p></Shell>;
  if (err && !records.length) return <Shell><h2>Link Unavailable</h2><p style={{ color: 'var(--text3)' }}>{err}</p></Shell>;

  const { firstName } = state;

  if (allDone) {
    return (
      <Shell>
        <div style={{ background: 'var(--success-bg)', color: 'var(--success)', padding: 20, borderRadius: 10, fontWeight: 600, textAlign: 'center', fontSize: 16 }}>
          ✓ All caught up{firstName ? `, ${firstName}` : ''}! No pending training right now.
        </div>
        <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 16, textAlign: 'center' }}>
          Questions? Contact <a href={`mailto:${CRM_EMAIL}`}>{CRM_EMAIL}</a>.
        </p>
      </Shell>
    );
  }

  const trainingModule = current?.training_module;

  return (
    <Shell>
      {courseTitle && (
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{courseTitle}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${((index) / records.length) * 100}%`, height: '100%', background: 'var(--copper)', transition: 'width 0.2s' }} />
        </div>
        <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{index + 1} of {records.length}</span>
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--copper)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        Section {trainingModule?.sequence || index + 1} · {trainingModule?.module_number}{trainingModule?.duration_minutes ? ` · ~${trainingModule.duration_minutes} min` : ''}
      </div>
      <h1 style={{ color: 'var(--accent)', margin: '0 0 4px', fontSize: 22 }}>{trainingModule?.title}</h1>
      <p style={{ color: 'var(--text3)', marginTop: 0, fontSize: 14 }}>Hi{firstName ? ` ${firstName}` : ''} — read through this training, then confirm below.</p>

      <div ref={contentRef} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 18, margin: '16px 0 8px', background: 'var(--bg2)', maxHeight: '65vh', overflowY: 'auto' }} className="training-link-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{trainingModule?.mobile_content || ''}</ReactMarkdown>
      </div>

      {needsScroll && !readToEnd && (
        <button
          onClick={() => contentRef.current?.scrollTo({ top: contentRef.current.scrollHeight, behavior: 'smooth' })}
          style={{ width: '100%', marginBottom: 12, background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', color: 'var(--warn)', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
          ↓ Keep reading — you can sign once you reach the end
        </button>
      )}

      {err && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}

      <div style={{ border: '1px solid var(--copper-border)', borderRadius: 10, padding: 18, background: 'var(--card-bg)' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: readToEnd ? 'var(--text)' : 'var(--text3)', marginBottom: 16, cursor: readToEnd ? 'pointer' : 'not-allowed' }}>
          <input type="checkbox" checked={ack} disabled={!readToEnd} onChange={e => setAck(e.target.checked)} style={{ width: 20, height: 20, marginTop: 1, flexShrink: 0 }} />
          I confirm I read and understood this training.
        </label>
        <input placeholder="Type your full name to sign" value={name} disabled={!readToEnd} onChange={e => setName(e.target.value)}
          style={{ width: '100%', padding: 14, border: '1px solid var(--border2)', borderRadius: 8, fontSize: 16, boxSizing: 'border-box', background: readToEnd ? 'var(--card-bg)' : 'var(--bg3)', color: 'var(--text)' }} />
        {name.trim() && (
          <div style={{ color: 'var(--accent)', fontFamily: 'Signature, cursive', fontSize: 18, margin: '10px 2px 4px' }}>{name.trim()}</div>
        )}
        <button disabled={busy || !name.trim() || !ack || !readToEnd} onClick={submit}
          style={{ marginTop: 14, width: '100%', background: (name.trim() && ack && readToEnd) ? 'var(--copper)' : 'var(--border2)', color: '#fff', border: 'none', borderRadius: 8, padding: 16, cursor: (name.trim() && ack && readToEnd) ? 'pointer' : 'default', fontWeight: 700, fontSize: 16 }}>
          {busy ? 'Submitting…' : index + 1 < records.length ? 'Complete & Next Section' : 'Complete Training'}
        </button>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 16, textAlign: 'center' }}>
        Questions? Contact <a href={`mailto:${CRM_EMAIL}`}>{CRM_EMAIL}</a>.
      </p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', justifyContent: 'center', padding: 16 }}>
      <SignatureFontStyle />
      <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 24, maxWidth: 720, width: '100%', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)' }}>
        {children}
      </div>
    </div>
  );
}
